import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Users, Undo2, Plus, Flag, Printer } from "lucide-react";
import { toast } from "sonner";
import { MissingWord, Loto, PhotoMemories, TurnQuiz, Riddles, PetitBac, Pendu } from "@/components/group/GroupStages";
import { GroupBlindTest, GROUP_BLINDTEST } from "@/components/group/GroupBlindTest";

import { GroupWhoSaid, WhoSaidCard } from "@/components/group/GroupWhoSaid";
import { GroupMorale, MoraleCard } from "@/components/group/GroupMorale";

export const GROUP_TYPES = {
  fable_whosaid: { label: "Qui a dit quoi ?", C: GroupWhoSaid, color: "#B45309" },
  fable_morale: { label: "Complète la morale", C: GroupMorale, color: "#C2410C" },
  music_blindtest: { label: "Blind test musical", C: GroupBlindTest, color: "#BE185D" },
  missing_word: { label: "Le mot qui manque", C: MissingWord, color: "#B45309" },
  loto: { label: "Loto des images", C: Loto, color: "#0F726A" },
  sound_loto: { label: "Loto des sons", C: Loto, color: "#0E7490" },
  photo_memories: { label: "Souvenirs en photos", C: PhotoMemories, color: "#7C3AED" },
  turn_quiz: { label: "Quiz à tour de rôle", C: TurnQuiz, color: "#2E8EC2" },
  riddles: { label: "Qui suis-je ?", C: Riddles, color: "#DB2777" },
  petit_bac: { label: "Petit bac", C: PetitBac, color: "#047857" },
  pendu: { label: "Le pendu", C: Pendu, color: "#9A3412" },
};

function Scoreboard({ players, points, group, award, undo }) {
  return (
    <div className="mb-5 rounded-3xl bg-white border-4 border-emerald-100 p-3" data-testid="gg-scoreboard">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-emerald-700 text-white font-patient font-black text-xl" data-testid="gg-group-score">
          <Users className="w-5 h-5" /> Groupe : {group}
        </span>
        <button onClick={() => award(null)} data-testid="gg-group-plus" className="h-12 px-4 rounded-2xl border-4 border-emerald-200 font-patient font-bold text-emerald-800 inline-flex items-center gap-1">
          <Plus className="w-5 h-5" /> Bonne réponse du groupe
        </button>
        {players.map((p) => (
          <button key={p.id} onClick={() => award(p.id)} data-testid={`gg-player-${p.id}`}
            className="h-12 px-4 rounded-2xl bg-amber-50 border-4 border-amber-200 font-patient font-bold text-gray-800 inline-flex items-center gap-2 active:scale-95 transition-transform">
            {p.name} <span className="min-w-8 h-8 px-2 rounded-full bg-amber-400 text-white inline-flex items-center justify-center">{points[p.id] || 0}</span>
          </button>
        ))}
        <button onClick={undo} data-testid="gg-undo" className="h-12 px-3 rounded-2xl text-gray-500 inline-flex items-center gap-1"><Undo2 className="w-5 h-5" /> Annuler</button>
      </div>
      <p className="text-xs text-gray-500 mt-1">Touchez le prénom de la personne qui trouve : un point pour elle et pour le groupe.</p>
    </div>
  );
}

const PRINTABLE = ["riddles", "petit_bac", "missing_word"];

function PrintSheets({ game, count }) {
  const [busy, setBusy] = useState(false);
  if (!PRINTABLE.includes(game.type)) return null;
  const copies = Math.max(1, count || 6);
  const print = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/group-games/${game.id}/print/pdf`, { params: { copies }, responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a");
      a.href = url; a.download = "jeu-de-groupe.pdf"; a.click(); URL.revokeObjectURL(url);
      toast.success(`Feuille de l'animateur et ${copies} feuille(s) résident prêtes à imprimer.`);
    } catch { toast.error("Impression impossible."); } finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={print} disabled={busy} data-testid={`gg-print-sheets-${game.id}`} className="h-12 rounded-2xl font-bold">
      {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Printer className="w-5 h-5 mr-2" />} Imprimer ({copies} {copies > 1 ? "feuilles" : "feuille"} + animateur)
    </Button>
  );
}

export function GroupGamePlayer({ game, players, onFinish }) {
  const [points, setPoints] = useState({});
  const [group, setGroup] = useState(0);
  const [scoring, setScoring] = useState(game.type !== "music_blindtest");
  const log = useRef([]);
  const start = useRef(Date.now());
  const award = (pid) => {
    log.current.push(pid);
    setGroup((g) => g + 1);
    if (pid) setPoints((p) => ({ ...p, [pid]: (p[pid] || 0) + 1 }));
  };
  const undo = () => {
    if (!log.current.length) return;
    const pid = log.current.pop();
    setGroup((g) => Math.max(0, g - 1));
    if (pid) setPoints((p) => ({ ...p, [pid]: Math.max(0, (p[pid] || 0) - 1) }));
  };
  const end = () => onFinish({ game, points: Object.fromEntries(players.map((p) => [p.id, points[p.id] || 0])), group, duration: Math.round((Date.now() - start.current) / 1000) });
  const T = GROUP_TYPES[game.type];
  return (
    <div data-testid="gg-player">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-serif-display text-3xl sm:text-4xl" style={{ color: T.color }}>{game.title}</h2>
        <div className="flex flex-wrap gap-2">
          <PrintSheets game={game} count={players.length} />
          <Button variant="outline" onClick={end} data-testid="gg-finish" className="h-12 rounded-2xl font-bold"><Flag className="w-5 h-5 mr-2" /> Terminer</Button>
        </div>
      </div>
      {scoring && <Scoreboard players={players} points={points} group={group} award={award} undo={undo} />}
      <T.C game={game} players={players} award={award} onEnd={end} setScoring={setScoring} />
    </div>
  );
}

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
export const seasonLabel = (season) => { const [y, m] = season.split("-"); return `${MONTHS[Number(m) - 1]} ${y}`; };

function SeasonGames({ games, onPick, players }) {
  const latest = games.map((g) => g.season).filter(Boolean).sort().pop();
  const list = games.filter((g) => g.season && g.season === latest);
  if (!list.length) return null;
  return (
    <section className="rounded-3xl bg-amber-50 border-4 border-amber-200 p-5" data-testid="gg-season-section">
      <h3 className="font-serif-display text-2xl text-amber-900 mb-1">Jeux de saison · {seasonLabel(latest)}</h3>
      <p className="text-amber-800 mb-4">Trois nouveaux jeux du mois, prêts à imprimer pour vos animations.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((g) => (
          <div key={g.id} className="flex flex-col gap-2">
            <button onClick={() => onPick(g)} data-testid={`gg-season-card-${g.id}`}
              className="flex-1 text-left rounded-3xl bg-white border-4 border-amber-100 hover:border-amber-300 p-5 transition-colors">
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: GROUP_TYPES[g.type]?.color }}>{GROUP_TYPES[g.type]?.label}</p>
              <p className="font-patient font-black text-xl text-gray-800">{g.title}</p>
              <p className="text-gray-500 mt-1">{g.description}</p>
            </button>
            <PrintSheets game={g} count={players} />
          </div>
        ))}
      </div>
    </section>
  );
}

function BlindTestCard({ onPick }) {
  const [count, setCount] = useState(0);
  useEffect(() => { api.get("/music").then((r) => setCount(r.data.length)).catch(() => {}); }, []);
  if (count < 3) return null;
  return (
    <button onClick={() => onPick(GROUP_BLINDTEST)} data-testid="gg-blindtest-card"
      className="w-full text-left rounded-3xl bg-rose-50 border-4 border-rose-200 hover:border-rose-300 p-5 transition-colors flex items-center gap-4">
      <span className="w-14 h-14 rounded-2xl bg-rose-500 flex items-center justify-center shrink-0"><Users className="w-7 h-7 text-white" /></span>
      <span>
        <span className="block font-serif-display text-2xl text-rose-800">Blind test musical</span>
        <span className="block text-rose-700">{Math.min(10, count)} chansons au hasard dans votre bibliothèque : les résidents devinent, vous révélez la réponse.</span>
      </span>
    </button>
  );
}

export function GroupGamesList({ onPick, players = 6 }) {
  const [games, setGames] = useState(null);
  useEffect(() => { api.get("/group-games").then((r) => setGames(r.data)).catch(() => setGames([])); }, []);
  if (!games) return <Loader2 className="w-8 h-8 animate-spin text-emerald-700 mx-auto" />;
  if (!games.length) return <><BlindTestCard onPick={onPick} /><WhoSaidCard onPick={onPick} /><MoraleCard onPick={onPick} /><p className="text-xl font-patient text-gray-500 text-center py-10" data-testid="gg-empty">Aucun jeu de groupe publié pour l'instant.</p></>;
  return (
    <div className="space-y-8" data-testid="gg-list">
      <BlindTestCard onPick={onPick} />
      <WhoSaidCard onPick={onPick} />
      <MoraleCard onPick={onPick} />
      <SeasonGames games={games} onPick={onPick} players={players} />
      {Object.entries(GROUP_TYPES).map(([type, T]) => {
        const list = games.filter((g) => g.type === type && !g.season);
        if (!list.length) return null;
        return (
          <section key={type}>
            <h3 className="font-serif-display text-2xl mb-3" style={{ color: T.color }}>{T.label}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((g) => (
                <div key={g.id} className="flex flex-col gap-2">
                  <button onClick={() => onPick(g)} data-testid={`gg-card-${g.id}`}
                    className="flex-1 text-left rounded-3xl bg-white border-4 border-gray-100 hover:border-emerald-200 p-5 transition-colors">
                    <p className="font-patient font-black text-xl text-gray-800">{g.title}</p>
                    <p className="text-gray-500 mt-1">{g.description}</p>
                  </button>
                  <PrintSheets game={g} count={players} />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
